import { type ReactNode } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsVenn from 'highcharts/modules/venn';

import { EmmLegend, type EmmLegendItem } from '../EmmLegend';

import type { SxProps, Theme } from '@mui/material';

// Register the venn module once at import time. Idempotent in Highcharts 11.
HighchartsVenn(Highcharts);

/**
 * Build a CSS `color-mix` chain that evenly blends the participating set colors,
 * then applies a target alpha by mixing with `transparent`. Returns `undefined`
 * when the `colors` map doesn't cover at least two of the participating sets —
 * Highcharts then falls back to its palette for that intersection.
 */
const blendOverlapColor = (setNames: string[], colors: Record<string, string> | undefined): string | undefined => {
  if (!colors) return undefined;
  const setColors = setNames.map(name => colors[name]).filter((c): c is string => Boolean(c));
  if (setColors.length < 2) return undefined;

  // Equal-weighted blend: start with the first color, then mix the next one
  // in at 1/(n+1) weight so the final result is an arithmetic average across
  // all participating colors regardless of order.
  let mixed = setColors[0];
  for (let i = 1; i < setColors.length; i++) {
    const newWeight = Math.round(100 / (i + 1));
    mixed = `color-mix(in srgb, ${mixed} ${100 - newWeight}%, ${setColors[i]} ${newWeight}%)`;
  }

  // Apply target alpha (45%) by mixing the result with transparent.
  const transparentPct = 55;
  return `color-mix(in srgb, ${mixed}, transparent ${transparentPct}%)`;
};

interface EmmVennOptionsArgs {
  points: EmmVennPoint[];
  colors: Record<string, string> | undefined;
  showLabels: boolean;
  height: number;
  theme: Theme;
}

// Module-level factory so the `formatter()` `this` binding survives React
// Compiler optimization (a ThisExpression in the component body can't be
// compiled). Threads through exactly the values the options reference.
const buildVennOptions = ({ points, colors, showLabels, height, theme }: EmmVennOptionsArgs): Highcharts.Options => ({
  chart: {
    type: 'venn',
    backgroundColor: 'transparent',
    height,
    spacing: [8, 0, 8, 0],
    style: { fontFamily: theme.typography.fontFamily }
  },
  title: { text: undefined },
  legend: { enabled: false },
  credits: { enabled: false },
  accessibility: { enabled: false },
  tooltip: {
    useHTML: true,
    backgroundColor: theme.palette.background.surface,
    borderColor: theme.palette.divider,
    borderRadius: 4,
    borderWidth: 1,
    padding: 8,
    shadow: true,
    style: {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: '12px',
      fontWeight: '400'
    },
    formatter() {
      const point = this.point as Highcharts.Point & { sets?: string[]; value?: number };
      const setsLabel = point.sets?.join(' ∩ ') ?? '';
      return `<span style="opacity:0.6;font-size:11px">${setsLabel}</span><br/><b style="font-weight:600">${
        point.value ?? 0
      }</b>`;
    }
  },
  plotOptions: {
    venn: {
      borderWidth: 2,
      borderColor: theme.palette.background.default,
      dataLabels: {
        enabled: showLabels,
        style: {
          color: theme.palette.text.primary,
          fontSize: '14px',
          fontWeight: '600',
          textOutline: 'none'
        }
      }
    }
  },
  series: [
    {
      type: 'venn',
      name: 'Venn',
      // Single-set points get a faint tint of their set color. Intersection
      // points get a CSS `color-mix` blend of the participating sets' colors
      // — so red ∩ blue actually reads as purple, not Highcharts' default
      // palette pick. The blend evaluates client-side in the SVG renderer,
      // which accepts modern CSS color functions in the `fill` attribute.
      data: points.map(p => {
        if (p.color) {
          return { sets: p.sets, value: p.value, color: p.color };
        }
        if (p.sets.length === 1) {
          const setColor = colors?.[p.sets[0]];
          if (setColor) {
            return {
              sets: p.sets,
              value: p.value,
              color: alpha(setColor, 0.2),
              borderColor: alpha(setColor, 0.7)
            };
          }
          return { sets: p.sets, value: p.value };
        }
        // Intersection — blend the participating sets' colors.
        const blend = blendOverlapColor(p.sets, colors);
        if (blend) {
          return { sets: p.sets, value: p.value, color: blend };
        }
        return { sets: p.sets, value: p.value };
      })
    } as Highcharts.SeriesVennOptions
  ]
});

export interface EmmVennPoint {
  /** Set names this point belongs to. Single set → solo segment; multiple sets → intersection. */
  sets: string[];
  /** The size of the segment / intersection. */
  value: number;
  /** Optional explicit color override for this point. */
  color?: string;
}

export interface EmmVennDiagramProps {
  points: EmmVennPoint[];
  /** Resolved-color map for set names. If omitted, Highcharts picks defaults. */
  colors?: Record<string, string>;
  /** Show numeric values inside each segment. Default `true`. */
  showLabels?: boolean;
  /**
   * Render an `EmmLegend` alongside the chart with one item per set (dot + name + `(count)`).
   * Counts come from the corresponding solo `points` entry's `value` (the set total).
   * Defaults to `false` — venn segments are usually self-labeling.
   */
  showLegend?: boolean;
  /** Where the legend renders relative to the chart when `showLegend` is on. Default `'bottom'`. */
  legendPosition?: 'bottom' | 'right';
  height?: number;
  title?: ReactNode;
  sx?: SxProps<Theme>;
}

export const EmmVennDiagram = ({
  points,
  colors,
  showLabels = true,
  showLegend = false,
  legendPosition = 'bottom',
  height = 320,
  title,
  sx
}: EmmVennDiagramProps) => {
  const theme = useTheme();
  const isSideBySide = legendPosition === 'right';

  // Legend items derived from solo points + the color map. Each set's `count`
  // is its solo entry's `value` (the set total). Sets without a solo entry are
  // dropped — there's no total to show. Sets without a color in `colors` fall
  // back to `text.disabled` so the legend stays legible.
  const legendItems: EmmLegendItem[] = showLegend
    ? points.reduce<EmmLegendItem[]>((acc, p) => {
        if (p.sets.length === 1) {
          const setName = p.sets[0];
          acc.push({
            key: setName,
            label: setName,
            color: colors?.[setName] ?? theme.palette.text.disabled,
            count: p.value
          });
        }
        return acc;
      }, [])
    : [];

  const options = buildVennOptions({ points, colors, showLabels, height, theme });

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, ...sx }}>
      {title != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {typeof title === 'string' ? (
            <Typography variant="subtitle2" color="text.primary">
              {title}
            </Typography>
          ) : (
            title
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSideBySide ? 'row' : 'column',
          alignItems: isSideBySide ? 'center' : 'stretch',
          gap: isSideBySide ? 3 : 1.5,
          flex: 1,
          minWidth: 0
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <HighchartsReact highcharts={Highcharts} options={options} immutable />
        </Box>

        {showLegend && legendItems.length > 0 && (
          <EmmLegend
            orientation={isSideBySide ? 'vertical' : 'horizontal'}
            items={legendItems}
            sx={isSideBySide ? undefined : { width: '100%' }}
          />
        )}
      </Box>
    </Box>
  );
};
